import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import MinimalLayout from '../components/layout/MinimalLayout';
import { 
  CheckCircleIcon,
  StarIcon,
  MapPinIcon,
  CalendarDaysIcon,
  ClockIcon,
  UserIcon,
  ChatBubbleLeftRightIcon,
  HomeIcon
} from '@heroicons/react/24/solid';

interface Teacher {
  id: string;
  name: string;
  instruments: string[];
  specialties: string[];
  rating: number;
  totalReviews: number;
  hourlyRate: number;
  location: string;
  distance?: string;
  experience: string;
  lessonFormats: string[];
}

interface StudentPreferences {
  instrument: string;
  genres: string[];
  skillLevel: string;
  lessonLocation: string[];
  age: string;
  learnerType: 'learner' | 'guardian';
  learnerFirstName?: string;
}

interface BookingData {
  lessonType: string;
  selectedDate: string;
  selectedTime: string;
  duration: number;
  totalPrice: number;
  specialRequests?: string;
}

const BookingConfirmationPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  const { teacher, studentPreferences, bookingData } = location.state || {};

  // Log booking confirmation page access
  console.log('Booking confirmation page loaded');
  console.log('Teacher:', teacher);
  console.log('Booking data:', bookingData);
  console.log('Student preferences:', studentPreferences);

  if (!teacher || !bookingData) {
    console.warn('Missing booking data, redirecting to browse teachers');
    navigate('/browse-teachers');
    return null;
  }

  const isGuardian = studentPreferences?.learnerType === 'guardian';
  const learnerName = studentPreferences?.learnerFirstName || 'you';

  const renderStars = (rating: number) => {
    return (
      <div className="flex items-center space-x-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <StarIcon
            key={star}
            className={`w-4 h-4 ${star <= rating ? 'text-yellow-400' : 'text-gray-300'}`}
          />
        ))}
      </div>
    );
  };

  const handleContactTeacher = () => {
    console.log('Opening contact with teacher:', teacher.name);
    // TODO: Open messaging interface
    alert('Messaging feature coming soon! The teacher will contact you shortly.');
  };

  const handleBackToHome = () => {
    navigate('/', { replace: true });
  };

  const handleViewProfile = () => {
    navigate('/student-dashboard', { 
      state: { studentData: studentPreferences } 
    });
  };

  // Generate a mock booking reference number
  const bookingReference = `LLX${Date.now().toString().slice(-6)}`;

  return (
    <MinimalLayout noPadding>
      <div className="min-h-screen bg-gray-50">
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          
          {/* Success Header */}
          <div className="text-center mb-8">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircleIcon className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Request Sent!</h1>
            <p className="text-lg text-gray-600">
              Your lesson request has been sent to {teacher.name}.
            </p>
                          <div className="mt-4 inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                Request Reference: {bookingReference}
              </div>
          </div>

          {/* Booking Details Card */}
                      <div className="bg-white rounded-lg shadow-lg border overflow-hidden mb-8">
            
            {/* Teacher Info Header */}
            <div className="bg-blue-50 px-6 py-4 border-b">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center">
                  <UserIcon className="w-6 h-6 text-gray-500" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">{teacher.name}</h2>
                  <div className="flex items-center space-x-3 mt-1">
                    {renderStars(teacher.rating)}
                    <span className="text-gray-600">({teacher.totalReviews} reviews)</span>
                    <span className="text-gray-600">•</span>
                    <span className="text-gray-600">{teacher.experience}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Lesson Details */}
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Requested Lesson Details</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <CalendarDaysIcon className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div>
                      <div className="font-medium text-gray-900">Date & Time</div>
                      <div className="text-gray-600">{bookingData.selectedDate}, {bookingData.selectedTime}</div>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-3">
                    <ClockIcon className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div>
                      <div className="font-medium text-gray-900">Duration</div>
                      <div className="text-gray-600">{bookingData.duration} minutes</div>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <MapPinIcon className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div>
                      <div className="font-medium text-gray-900">Lesson Type</div>
                      <div className="text-gray-600">{bookingData.lessonType}</div>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-3">
                    <div className="w-5 h-5 text-gray-400 mt-0.5 font-bold">$</div>
                    <div>
                      <div className="font-medium text-gray-900">Total Cost</div>
                      <div className="text-gray-600">
                        ${(bookingData.duration === 30 ? teacher.hourlyRate / 2 : teacher.hourlyRate).toFixed(2)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Special Requests */}
              {bookingData.specialRequests && (
                <div className="mt-6 pt-4 border-t">
                  <div className="font-medium text-gray-900 mb-2">Special Requests</div>
                  <div className="text-gray-600 bg-gray-50 rounded-lg p-3">
                    {bookingData.specialRequests}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Next Steps */}
          <div className="bg-white rounded-lg shadow-sm border p-6 mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">What happens next?</h3>
            
            <div className="space-y-4">
              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-blue-600 font-semibold text-sm">1</span>
                </div>
                <div>
                  <div className="font-medium text-gray-900">Teacher Reviews Request</div>
                  <div className="text-gray-600">
                    {teacher.name} will receive your lesson request and respond within 24 hours.
                  </div>
                </div>
              </div>
              
              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-blue-600 font-semibold text-sm">2</span>
                </div>
                <div>
                  <div className="font-medium text-gray-900">Lesson Confirmation</div>
                  <div className="text-gray-600">
                    Once approved, your teacher will reach out with final details and setup instructions.
                  </div>
                </div>
              </div>
              
              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-blue-600 font-semibold text-sm">3</span>
                </div>
                <div>
                  <div className="font-medium text-gray-900">Lesson Time</div>
                  <div className="text-gray-600">
                    Attend your lesson and start your musical journey!
                  </div>
                </div>
              </div>
              
              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-blue-600 font-semibold text-sm">4</span>
                </div>
                <div>
                  <div className="font-medium text-gray-900">Payment</div>
                  <div className="text-gray-600">
                    Payment will be processed securely after your lesson is completed.
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Important Notes */}
          <div className="bg-yellow-50 rounded-lg border border-yellow-200 p-6 mb-8">
            <h3 className="text-lg font-semibold text-yellow-800 mb-3">Important Notes</h3>
            <ul className="space-y-2 text-yellow-800">
              <li className="flex items-start space-x-2">
                <span>•</span>
                <span>You can reschedule or cancel up to 24 hours before your lesson</span>
              </li>
              <li className="flex items-start space-x-2">
                <span>•</span>
                <span>A confirmation email has been sent to your registered email address</span>
              </li>
              <li className="flex items-start space-x-2">
                <span>•</span>
                <span>For {bookingData.lessonType === 'Online' ? 'online lessons, ensure you have a stable internet connection' : bookingData.lessonType === 'On-site (teacher\'s studio)' ? 'studio lessons, arrive 5 minutes early' : 'in-home lessons, ensure a quiet practice space'}</span>
              </li>
            </ul>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => {
                console.log('Proceeding to payment for lesson');
                navigate('/payment', {
                  state: {
                    teacher,
                    bookingData,
                    studentData: studentPreferences
                  }
                });
              }}
              className="flex items-center justify-center space-x-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <span className="text-lg">💳</span>
              <span>Proceed to Payment</span>
            </button>
            
            <button
              onClick={handleContactTeacher}
              className="flex items-center justify-center space-x-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <ChatBubbleLeftRightIcon className="w-5 h-5" />
              <span>Contact Teacher</span>
            </button>
            
            <button
              onClick={handleViewProfile}
              className="flex items-center justify-center space-x-2 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <UserIcon className="w-5 h-5" />
              <span>View My Dashboard</span>
            </button>
            
            <button
              onClick={handleBackToHome}
              className="flex items-center justify-center space-x-2 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <HomeIcon className="w-5 h-5" />
              <span>Back to Home</span>
            </button>
          </div>

          {/* Success Message for Guardian */}
          {isGuardian && (
            <div className="mt-8 bg-green-50 rounded-lg border border-green-200 p-6 text-center">
              <h4 className="font-semibold text-green-800 mb-2">
                Great choice for {learnerName}!
              </h4>
              <p className="text-green-700">
                You've successfully requested a {studentPreferences.instrument.toLowerCase()} lesson. 
                {learnerName} is going to love learning with {teacher.name}!
              </p>
            </div>
          )}
        </div>
      </div>
    </MinimalLayout>
  );
};

export default BookingConfirmationPage; 